import styled from '@emotion/styled';
import React, { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import type { FreeTextbooksSubtopicViewFooterQuery } from '__free_textbooks_generated__/FreeTextbooksSubtopicViewFooterQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import TriangleDownIcon from 'ms-components/icons/TriangleDown';
import {
  ACCORDION_ANIMATION_DURATION,
  AccordionItem,
} from 'ms-pages/Teacher/components/Accordion';
import { font, fontFamily, fontWeight } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import Separator from 'ms-ui-primitives/Separator';
import { multilineTextOverflow, tappable } from 'ms-utils/emotion';
import keyDownMap from 'ms-utils/keyDownMap';

import { FeaturesSection } from './FreeTextbooksFeaturesSection';

const Wrapper = styled.div({
  fontFamily: fontFamily.body,
  fontWeight: fontWeight.normal,
  color: colors.regentGray,
  fontSize: 14,
  lineHeight: '18px',
});

const FooterElementTitle = styled.h3({
  color: colors.cloudBurst,
  ...multilineTextOverflow(2),
  ...font.gilroyHeading,
  ...tappable,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 16,
});

const TriangleIconWrapper = styled.div<{ isRotated: boolean }>(
  {
    // This matches the accordion animation
    transition: `transform ${ACCORDION_ANIMATION_DURATION / 1000}s linear`,
  },
  ({ isRotated }) =>
    isRotated && {
      transform: 'rotate(180deg)',
    },
);

const LineSeparator = styled.div({
  height: 2,
  backgroundColor: colors.grey90,
  marginTop: 24,
  marginBottom: 24,
});

const Outcome = styled.div({
  paddingTop: 24,
});
const OutcomeCode = styled.h4({
  fontWeight: fontWeight.semibold,
  fontSize: 14,
  lineHeight: '18px',
  color: colors.cloudBurst,
});

const OutcomeDescription = styled.p({
  fontWeight: fontWeight.normal,
  fontSize: 14,
  lineHeight: '18px',
  color: colors.shuttleGray,
});

const AboutText = styled.p({
  fontWeight: fontWeight.normal,
  fontSize: 14,
  lineHeight: '18px',
  color: colors.regentGray,
  paddingTop: 16,
  paddingBottom: 16,
});

const FREE_TEXTBOOKS_SUBTOPIC_VIEW_QUERY = graphql`
  query FreeTextbooksSubtopicViewFooterQuery($subtopicId: ID!) {
    subtopic(id: $subtopicId) {
      id
      outcomes {
        id
        code
        description
      }
    }
  }
`;

type Item = 'outcomes' | 'features' | 'about';

const INITIAL_OPEN_ITEM: Item = 'outcomes';

type Props = { subtopicId: string };

export function FreeTextbooksSubtopicViewFooter({ subtopicId }: Props) {
  return (
    <ErrorBoundaryWithRetry name="FreeTextbooksSubtopicViewFooter">
      {({ fetchKey }) => (
        <Suspense fallback={<div>Loading...</div>}>
          <FreeTextbooksSubtopicViewFooterInner
            subtopicId={subtopicId}
            fetchKey={fetchKey}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeTextbooksSubtopicViewFooterInner({
  subtopicId,
  fetchKey,
}: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<FreeTextbooksSubtopicViewFooterQuery>(
    FREE_TEXTBOOKS_SUBTOPIC_VIEW_QUERY,
    { subtopicId },
    { fetchKey },
  );

  const { subtopic } = data;

  const [openItem, setOpenItem] = React.useState<Item | null>(
    INITIAL_OPEN_ITEM,
  );

  const toggleItem = React.useCallback(
    (item: Item) => {
      if (openItem === item) setOpenItem(null);
      else setOpenItem(item);
    },
    [openItem],
  );

  return (
    <Wrapper>
      {subtopic != null && subtopic.outcomes.length > 0 && (
        <>
          <FooterElement
            elemKey="outcomes"
            label="Outcomes"
            isOpen={openItem === 'outcomes'}
            toggleItem={toggleItem}
          />

          <AccordionItem isOpen={openItem === 'outcomes'}>
            {subtopic.outcomes.map(outcome => (
              <Outcome key={outcome.id}>
                <OutcomeCode>{outcome.code}</OutcomeCode>
                <OutcomeDescription>{outcome.description}</OutcomeDescription>
              </Outcome>
            ))}
          </AccordionItem>
          <LineSeparator />
        </>
      )}

      <FooterElement
        elemKey="features"
        label="What is Mathspace"
        isOpen={openItem === 'features'}
        toggleItem={toggleItem}
      />
      <AccordionItem isOpen={openItem === 'features'}>
        <FeaturesSection />
      </AccordionItem>

      <LineSeparator />

      <FooterElement
        elemKey="about"
        label="About Mathspace"
        isOpen={openItem === 'about'}
        toggleItem={toggleItem}
      />
      <AccordionItem isOpen={openItem === 'about'}>
        <AboutText>
          It is our vision at Mathspace to be an all-in-one resource that helps
          teachers teach and students learn.
        </AboutText>

        <AboutText>
          Our global team consists of curriculum experts and content specialists
          who have created the most comprehensive standards-aligned online
          textbook materials on the market, designers and engineers who brought
          these materials to life through interactivity and adaptive learning
          smarts, and experienced educators who liaise and work together with
          our teaching community in shaping and continuously improving our
          product development.
        </AboutText>

        <AboutText>
          Our award-winning technology provides students help right when they
          need it, and our program simplifies and automates teacher tasks,
          delivering the most important student insights straight to your
          account, so that you can get the most value out of your time.
        </AboutText>
      </AccordionItem>

      <Separator size={8} />
    </Wrapper>
  );
}

function FooterElement({
  elemKey,
  label,
  isOpen,
  toggleItem,
}: {
  elemKey: Item;
  label: string;
  isOpen: boolean;
  toggleItem: (item: Item) => void;
}) {
  const toggle = React.useCallback(() => {
    toggleItem(elemKey);
  }, [elemKey, toggleItem]);

  return (
    <FooterElementTitle
      onClick={toggle}
      tabIndex={0}
      onKeyDown={keyDownMap({
        ENTER: [toggle, { preventDefault: true }],
        SPACE: [toggle, { preventDefault: true }],
      })}
    >
      {label}
      <TriangleIconWrapper isRotated={isOpen}>
        <TriangleDownIcon />
      </TriangleIconWrapper>
    </FooterElementTitle>
  );
}
